import type { JSX } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Download, Search } from 'lucide-react'
import { KNOWN_EVENT_KINDS, wsRoom } from '@apiary/shared'
import type { BotEventDto } from '@/lib/dto'
import { api } from '@/lib/api'
import { useBotEvents, useBots } from '@/hooks/queries'
import { useWsRoom } from '@/hooks/useWsRoom'
import { toast } from '@/stores/toasts'
import { Button } from '@/ui/primitives/Button'
import { Select } from '@/ui/primitives/Select'
import { Switch } from '@/ui/primitives/Switch'
import { Panel, SectionHead } from '@/ui/primitives/Surface'
import { EmptyBlock, ErrorBlock } from '@/screens/shared/states'
import { problemText } from '@/screens/shared/problem'
import { LogStream } from './LogStream'
import {
  EMPTY_LOG_QUERY,
  LEVEL_FILTERS,
  RANGE_FILTERS,
  filterLog,
  levelNotExportable,
  toExportQuery,
  type LevelFilter,
  type LogQuery,
  type RangeFilter
} from './log-filters'

/**
 * Экран «Логи» — консоль по всему флоту: выбираешь бота, фильтруешь, читаешь
 * живой поток.
 *
 * Ключевые решения:
 *
 *  1. Бот выбирается на экране, а не в адресе: сюда приходят «посмотреть логи», и
 *     переключение между ботами должно стоить один клик — без перезагрузки роутом
 *     и без потери накопленного live-буфера соседнего бота.
 *  2. История приезжает REST-запросом (`useBotEvents`), живые строки дописывает
 *     WS-редьюсер в ТОТ ЖЕ ключ кэша. Поэтому подписка на комнату бота
 *     обязательна: без неё консоль показывает срез на момент открытия и молча
 *     «замерзает» — самый неприятный вид поломки, потому что выглядит рабочим.
 *  3. Фильтры клиентские (почему — см. `log-filters.ts`), а CSV-экспорт
 *     серверный: в файле должна быть вся история за период, а не буфер экрана.
 *  4. Экспорт качается blob'ом. `<a href>` не подходит принципиально: токен живёт
 *     только в памяти (US-1 AC1), а ссылка не умеет ставить `Authorization`.
 */

const KIND_OPTIONS = [
  { value: '', label: 'Любой вид' },
  ...KNOWN_EVENT_KINDS.map(kind => ({ value: kind, label: kind }))
]

/**
 * Сколько строк истории просим у API. Дальше буфер живёт за счёт WS.
 *
 * Значение упирается в серверный потолок `MAX_EVENT_PAGE_LIMIT` (500) из
 * `apps/api/src/modules/events/dto/events.dto.ts`. Поднимать его здесь в
 * одиночку нельзя: API ответит 400 `VALIDATION_FAILED`, и экран покажет «Лог не
 * загрузился» — ровно этот баг и был в проде.
 */
export const HISTORY_LIMIT = 500

export function LogsScreen(): JSX.Element {
  const bots = useBots()
  const items = bots.data?.items ?? []

  const [botId, setBotId] = useState('')
  const [query, setQuery] = useState<LogQuery>(EMPTY_LOG_QUERY)
  const [wrap, setWrap] = useState(false)
  const [exporting, setExporting] = useState(false)
  const searchRef = useRef<HTMLInputElement>(null)

  // Первый бот выбирается сам: экран без выбранного бота бесполезен, а экран
  // «сначала выберите бота» — лишний шаг на пустом месте.
  const selected =
    botId !== '' && items.some(bot => bot.id === botId) ? botId : (items[0]?.id ?? '')
  useEffect(() => {
    if (selected !== botId) setBotId(selected)
  }, [selected, botId])

  useWsRoom(selected === '' ? null : wsRoom.bot(selected))
  const log = useBotEvents(selected, HISTORY_LIMIT)

  const events: BotEventDto[] = useMemo(() => log.data?.items ?? [], [log.data])
  // `Date.now()` берётся один раз на пересчёт, а не в каждой строке: иначе
  // граница диапазона «плывёт» внутри одного прохода по списку.
  const visible = useMemo(() => filterLog(events, query, Date.now()), [events, query])

  // `/` в поиск — то же сочетание, что на экране ботов.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      const target = event.target
      const typing =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement
      if (event.key === '/' && !typing) {
        event.preventDefault()
        searchRef.current?.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [])

  const botOptions = useMemo(
    () => items.map(bot => ({ value: bot.id, label: bot.name, note: bot.serverName })),
    [items]
  )

  const selectedBot = items.find(bot => bot.id === selected) ?? null
  const filtersActive =
    query.level !== 'all' || query.kind !== '' || query.search !== '' || query.range !== 'all'

  async function onExport(): Promise<void> {
    if (selectedBot === null) return
    setExporting(true)
    try {
      const blob = await api.bots.exportCsv(selectedBot.id, toExportQuery(query))
      download(blob, `${selectedBot.name}-events.csv`)
      toast('CSV с логами выгружен', 'success')
    } catch (error) {
      toast(problemText(error), 'danger')
    } finally {
      setExporting(false)
    }
  }

  // Ни скелетон, ни ошибка, ни пустой флот не показывают строку действий: без
  // списка ботов выбирать нечего, а выключенные фильтры — просто шум.
  if (bots.isPending) {
    return (
      <div className="shimmer h-[70dvh] rounded-md border border-line bg-panel" aria-hidden>
        <span className="sr-only">Загрузка</span>
      </div>
    )
  }

  if (bots.isError) {
    return (
      <ErrorBlock
        error={bots.error}
        onRetry={() => void bots.refetch()}
        title="Не удалось получить список ботов"
      />
    )
  }

  if (items.length === 0) {
    return (
      <EmptyBlock
        title="Логов пока нет"
        note="Лог принадлежит боту: создайте первого — и его события появятся здесь."
      />
    )
  }

  return (
    <>
      <SectionHead
        actions={
          <>
            <Select
              label="Бот"
              hideLabel
              value={selected}
              onValueChange={setBotId}
              options={botOptions}
              className="w-56"
            />
            <Button
              onClick={() => void onExport()}
              loading={exporting}
              title={
                levelNotExportable(query.level)
                  ? 'В CSV уйдут все уровни: контракт экспорта принимает только один уровень'
                  : 'Выгрузить отфильтрованные события в CSV'
              }
            >
              <Download aria-hidden className="size-4" />
              Экспорт CSV
            </Button>
          </>
        }
      />

      <Panel
        title={selectedBot === null ? 'Консоль' : `Консоль · ${selectedBot.name}`}
        note={
          visible.length === events.length
            ? `${events.length} строк`
            : `${visible.length} из ${events.length} строк`
        }
        bodyClassName="flex flex-col gap-3 p-3"
        actions={
          <>
            <Select
              label="Уровень"
              hideLabel
              value={query.level}
              onValueChange={value =>
                setQuery(previous => ({ ...previous, level: value as LevelFilter }))
              }
              options={LEVEL_FILTERS}
              className="w-56"
            />
            <Select
              label="Вид события"
              hideLabel
              value={query.kind}
              onValueChange={value => setQuery(previous => ({ ...previous, kind: value }))}
              options={KIND_OPTIONS}
              className="w-40"
            />
            <Select
              label="Период"
              hideLabel
              value={query.range}
              onValueChange={value =>
                setQuery(previous => ({ ...previous, range: value as RangeFilter }))
              }
              options={RANGE_FILTERS}
              className="w-48"
            />
            <div className="relative">
              <Search
                aria-hidden
                className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-ink-faint"
              />
              <input
                ref={searchRef}
                type="search"
                value={query.search}
                onChange={event =>
                  setQuery(previous => ({ ...previous, search: event.target.value }))
                }
                aria-label="Поиск по тексту события"
                placeholder="Текст события  /"
                className="h-9 w-52 rounded-sm border border-line bg-panel-2 py-1.5 pr-3 pl-8 text-base text-ink transition-colors duration-140 placeholder:text-ink-faint hover:border-line-strong focus:border-honey/60"
              />
            </div>
            <Switch label="Перенос" checked={wrap} onCheckedChange={setWrap} align="left" />
            <Button
              variant="ghost"
              size="sm"
              disabled={!filtersActive}
              onClick={() => setQuery(EMPTY_LOG_QUERY)}
            >
              Сбросить
            </Button>
          </>
        }
      >
        {log.isError ? (
          <ErrorBlock
            error={log.error}
            onRetry={() => void log.refetch()}
            title="Лог не загрузился"
          />
        ) : log.isPending ? (
          <div className="shimmer h-[58dvh] rounded-sm border border-line bg-panel-2" aria-hidden>
            <span className="sr-only">Загрузка лога</span>
          </div>
        ) : (
          <LogStream
            rows={visible}
            wrap={wrap}
            empty={
              events.length === 0
                ? 'У этого бота ещё нет событий. Как только он что-то сделает, строки появятся здесь.'
                : 'Под фильтр не попала ни одна строка — снимите часть условий.'
            }
            className="h-[58dvh]"
          />
        )}
      </Panel>
    </>
  )
}

/**
 * Скачивание blob'а. `URL.createObjectURL` + программный клик по временной
 * ссылке — единственный способ отдать файл, полученный запросом с заголовком
 * `Authorization`. `revokeObjectURL` обязателен: иначе blob висит в памяти до
 * перезагрузки страницы.
 */
function download(blob: Blob, filename: string): void {
  if (typeof URL.createObjectURL !== 'function') return
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.rel = 'noopener'
  document.body.append(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}
